/**
 * Derives the token the GraphQL endpoint expects in every request, from nothing but the current
 * clock. No secret is involved: the backend runs this same algorithm and compares the result, so
 * anyone willing to read this file can mint a valid token. That is accepted — it is a speed bump
 * against drive-by traffic, not authentication.
 *
 * This is a twin of `app/BaseComponents/RollingToken/FromTimeWindow.php`, and of the same file in
 * the whitelabel frontend's own repository. None of the three deploy together, so the algorithm
 * cannot be changed on one side alone: the leading version character exists so that a new one can
 * be rolled out while the server still accepts the old.
 *
 * The pinned vectors in `__tests__/unit/graphql/rollingToken.test.ts` are the same list asserted by
 * `tests/Integration/BaseComponents/RollingToken/FromTimeWindowTest.php`. If the two lists ever
 * disagree, the implementations have drifted apart.
 */

/** The header the backend reads. Named after nothing in particular, on purpose. */
export const ROLLING_TOKEN_HEADER = "X-Repareo-Client-Token";

const VERSION = "1";
const WINDOW_MILLISECONDS = 1800 * 1000;
const FNV_OFFSET_BASIS = 0x811c9dc5;
const FNV_PRIME = 0x01000193;

/**
 * FNV-1a, 32 bit, in its canonical form. Deliberately not exported: it agrees with its PHP twin
 * only for ASCII input, which everything built below is by construction, and nothing else may be
 * hashed with it.
 */
const fnv1a32 = (value: string): number => {
  let hash = FNV_OFFSET_BASIS;

  for (let index = 0; index < value.length; index += 1) {
    hash = Math.imul(hash ^ value.charCodeAt(index), FNV_PRIME) >>> 0;
  }

  return hash;
};

/** Always eight characters: a hash with a leading zero nibble would otherwise shorten the token. */
const asHex = (hash: number): string => hash.toString(16).padStart(8, "0");

/** The token for one specific window. Exported so the tests can pin the algorithm against literals. */
export const tokenForWindow = (timeWindow: number): string => {
  const seed = `rpo:${String(timeWindow).split("").reverse().join("")}:gql`;
  const first = fnv1a32(seed);

  return `${VERSION}${asHex(first)}${asHex(fnv1a32(seed + String(first)))}`;
};

/**
 * The token for right now.
 *
 * @param nowMilliseconds Injectable clock, so a test does not have to fake timers to pin a value.
 */
export const rollingToken = (nowMilliseconds: number = Date.now()): string =>
  tokenForWindow(Math.floor(nowMilliseconds / WINDOW_MILLISECONDS));
